import { FaMapMarked } from "react-icons/fa";
import { metadataWithoutParent } from "../page-fragments";
import brochureGrouping from "./brochureGrouping";
import { multilingualStringPreview } from "../base/multilingualArrayString";
import { defineField, defineType } from "sanity";
import { cruiseGroups } from "../data-types/cruiseGroups";
import allPageBuilderSections, {
  pageBuilderOptions
} from "../../common/allPageBuilderSections";
import { multilingualOrderingField } from "../../common/ordering";

export default defineType({
  name: "destination",
  title: "Destination",
  type: "document",
  icon: FaMapMarked,
  orderings: [
    {
      title: "Title",
      name: "titleAsc",
      by: [{ field: multilingualOrderingField("name"), direction: "asc" }]
    }
  ],

  preview: {
    select: {
      title: "name",
      subtitle: "title",
      media: "heroImage"
    },
    prepare({ title, subtitle, media }) {
      return {
        title: multilingualStringPreview(title),
        subtitle: multilingualStringPreview(subtitle),
        media
      };
    }
  },
  fieldsets: [
    {
      name: "hero",
      title: "Hero",
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "FYC",
      title: "Find Your Cruise",
      description:
        "Options for the find your cruise search on this destination",
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "opening",
      title: "Opening section",
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "emotional-section",
      title: "Emotional section",
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "custom-content-section",
      title: "Custom content section",
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "infographic-section",
      title: "Infographic Section",
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "travel-guide",
      title: "Travel guide section",
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "ships",
      title: "Our ships section",
      options: {
        collapsible: true,
        collapsed: true
      }
    },
    {
      name: "blog",
      title: "Blog",
      options: {
        collapsible: true,
        collapsed: true
      }
    }
  ],
  groups: [
    { name: "asCard", title: "Render as Card" },
    { name: "faq", title: "FAQ" },
    {
      name: "seo",
      title: "SEO, Routing & Tracking"
    }
  ],
  fields: [
    {
      name: "name",
      title: "Name",
      type: "multilingualArrayString",
      validation: (Rule) => Rule.required()
    },
    {
      name: "isExpeditionDestination",
      title: "Is Expedition Destination",
      type: "boolean",
      validation: (Rule) => Rule.required()
    },
    {
      name: "mediaType",
      title: "Media Type",
      type: "string",
      options: {
        list: ["video", "image"]
      },
      validation: (Rule) => Rule.required(),
      fieldset: "hero"
    },
    {
      name: "heroImage",
      title: "Hero Image",
      type: "image",
      options: { hotspot: true },
      fieldset: "hero"
    },
    {
      name: "video",
      type: "video",
      fieldset: "hero"
    },
    {
      name: "title",
      title: "Title",
      type: "multilingualArrayText",
      fieldset: "opening",
      //validation: (Rule) => Rule.required(),
      options: {
        rows: 3
      }
    },
    {
      name: "prefixArticle",
      title: "Prefix Article",
      description: "Article to be displayed on the destination page",
      type: "multilingualArrayString"
    },
    {
      name: "leadIn",
      title: "Lead-in",
      type: "multilingualArrayText",
      fieldset: "opening"
      //validation: (Rule) => Rule.required()
    },
    {
      name: "body",
      title: "Body",
      type: "multilingualArrayBlockContent",
      fieldset: "opening"
    },
    {
      name: "customContentSectionMenuName",
      title: "Custom Content Section Menu Name",
      type: "multilingualArrayString",
      description: "if empty no menu item will be shown",
      fieldset: "custom-content-section"
    },
    defineField({
      name: "sections",
      type: "array",
      title: "Page section",
      description:
        'Example: use this section to explain the difference between Antarctica Cruise and Antarctica Fly Cruise. Configure it as "Card Mosaic". (Max 1 item)',
      of: allPageBuilderSections(),
      validation: (Rule) => Rule.max(1),
      options: {
        ...pageBuilderOptions
      },
      fieldset: "custom-content-section"
    }),
    {
      name: "fycShowExpeditionFilter",
      title: "Show Expeditions filter",
      type: "boolean",
      fieldset: "FYC"
    },
    {
      name: "fycSpecialType",
      title: "Special Type Filter",
      type: "string",
      options: {
        list: [
          { title: "Top Cruise", value: "TopCruise" },
          { title: "Antarctica Bridge", value: "AntarcticaBridge" }
        ]
      },
      fieldset: "FYC"
    },
    {
      name: "slideshowImages",
      title: "Slideshow Images",
      type: "array",
      of: [
        {
          type: "slide"
        }
      ]
    },
    {
      name: "allInclusiveDisclaimer",
      type: "multilingualArrayText",
      title: "Disclaimer",
      fieldset: "infographic-section"
      //validation: (Rule) => Rule.required()
    },

    {
      name: "hideTravelGuide",
      title: "Hide Brochure section",
      description:
        "If enabled, the brochure section and its sticky menu entry are hidden",
      type: "boolean",
      initialValue: false,
      fieldset: "travel-guide"
    },
    {
      name: "travelGuideImage",
      title: "Image",
      type: "image",
      options: { hotspot: true },
      fieldset: "travel-guide"
      //validation: (Rule) => Rule.required()
    },
    {
      name: "travelGuideTitle",
      title: "Title",
      type: "multilingualArrayString",
      fieldset: "travel-guide"
      //validation: (Rule) => Rule.required()
    },
    {
      name: "travelGuideHeader",
      title: "Header",
      type: "multilingualArrayString",
      fieldset: "travel-guide"
      //validation: (Rule) => Rule.required()
    },
    {
      name: "travelGuideBody",
      title: "Body",
      type: "multilingualArrayText",
      fieldset: "travel-guide"
      //validation: (Rule) => Rule.required()
    },
    {
      name: "brochureGroup",
      title: "Brochure Group",
      type: "reference",
      to: [{ type: brochureGrouping.name }],
      fieldset: "travel-guide"
    },
    {
      name: "travelGuideButtonText",
      title: "Button Text",
      type: "multilingualArrayString",
      fieldset: "travel-guide"
      //validation: (Rule) => Rule.required()
    },
    {
      name: "expeditionTeam",
      title: "Expedition Team",
      type: "array",
      of: [
        {
          type: "reference",
          to: [{ type: "expeditionTeamMember" }]
        }
      ]
    },
    {
      name: "blog",
      title: "Blog Posts",
      type: "blogSection",
      fieldset: "blog"
    },
    {
      name: "discoverBlogCategoryId",
      title: "Discover Blog Category Id",
      type: "number",
      readOnly: true,
      fieldset: "blog"
    },
    {
      name: "shipsSectionTitle",
      title: "Title",
      type: "multilingualArrayString",
      fieldset: "ships"
    },
    {
      name: "visitingShips",
      title: "Visiting Ships",
      type: "array",
      fieldset: "ships",
      of: [
        {
          name: "ship",
          title: "Ship",
          type: "reference",
          to: [{ type: "ship" }]
        }
      ]
    },
    {
      name: "faqSection",
      title: "FAQ",
      type: "faqSection",
      group: "faq",
      options: { collapsible: true, collapsed: true }
    },
    {
      name: "metadata",
      title: "SEO, Routing & Tracking",
      type: metadataWithoutParent.name,
      group: "seo"
    },
    {
      name: "card",
      title: "Render as Card",
      type: "asCard",
      group: "asCard"
    },
    {
      name: "destinationId",
      title: "Destination ID (in Shop API/SMA/Setup)",
      type: "number",
      validation: (Rule) => Rule.required().integer().positive()
    },
    {
      name: "destinationWebCode",
      title: "Destination WebCode",
      type: "string",
      validation: (Rule) => Rule.required()
    },
    {
      name: "cruiseGroup",
      title: "Cruise Group",
      type: "string",
      validation: (Rule) => Rule.required(),
      options: {
        list: cruiseGroups
      }
    }
  ]
});
